/**
 * ChangePinDialog.jsx
 *
 * Modal for changing an existing PIN (or setting one for the first time
 * outside of forced setup, e.g. a SuperAdmin opting in voluntarily).
 *
 * Behaviour:
 *  - If the user has a PIN, currentPin is required and verified server-side.
 *  - If the user has no PIN, currentPin is skipped.
 *  - On success, refreshes status via the PinLockContext indirectly (the
 *    setPin call returns and the dialog closes; lock state isn't changed).
 */

import React, { useEffect, useState } from 'react';
import { setPin, getPinStatus } from '../api/client.js';

const PIN_MIN = 6;
const PIN_MAX = 6;

export default function ChangePinDialog({ onClose }) {
  const [hasExisting, setHasExisting] = useState(null);
  const [currentPin, setCurrentPin]   = useState('');
  const [newPin, setNewPin]           = useState('');
  const [confirmPin, setConfirmPin]   = useState('');
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState(null);
  const [done, setDone]               = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { self } = await getPinStatus();
        setHasExisting(!!self.isSet);
      } catch (e) {
        setError(e.message);
        setHasExisting(false);
      }
    })();
  }, []);

  async function submit(e) {
    e?.preventDefault();
    setError(null);
    if (newPin.length < PIN_MIN) return setError(`PIN must be at least ${PIN_MIN} digits.`);
    if (!/^\d+$/.test(newPin))   return setError('PIN must be digits only.');
    if (/^(\d)\1+$/.test(newPin)) return setError('PIN cannot be a single repeating digit.');
    if (newPin !== confirmPin)   return setError('PINs do not match.');
    if (hasExisting && !currentPin) return setError('Current PIN is required.');

    setBusy(true);
    try {
      await setPin(newPin, hasExisting ? currentPin : undefined);
      setDone(true);
      setTimeout(onClose, 800);
    } catch (e) {
      setError(e.message || 'Could not save PIN.');
    } finally { setBusy(false); }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="changepin-title"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 8500,
        background: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: '100%', maxWidth: 380,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 14, padding: 22,
          boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
        }}
      >
        <h2 id="changepin-title" style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
          {hasExisting ? 'Change session PIN' : 'Set session PIN'}
        </h2>
        <p style={{ fontSize: 12, color: 'var(--text-3)', margin: '4px 0 16px' }}>
          {PIN_MIN}-digit PIN. Used to unlock the app after inactivity.
        </p>

        {hasExisting && (
          <Field label="Current PIN" value={currentPin} onChange={setCurrentPin} autoFocus disabled={busy} />
        )}
        <Field label="New PIN" value={newPin} onChange={setNewPin} autoFocus={!hasExisting} disabled={busy} />
        <Field label="Confirm new PIN" value={confirmPin} onChange={setConfirmPin} disabled={busy} />

        {error && (
          <div className="callout danger" style={{ marginTop: 12, fontSize: 12, padding: '8px 10px' }}>
            <div style={{ flex: 1 }}>{error}</div>
          </div>
        )}
        {done && (
          <div className="callout brand" style={{ marginTop: 12, fontSize: 12, padding: '8px 10px' }}>
            <div style={{ flex: 1 }}>PIN updated.</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy} style={{ flex: 1 }}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy || hasExisting === null} style={{ flex: 1 }}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, autoFocus, disabled }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-3)', marginBottom: 4 }}>{label}</div>
      <input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        pattern="[0-9]*"
        maxLength={PIN_MAX}
        value={value}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
        style={{
          width: '100%', padding: '10px 12px', fontSize: 14,
          letterSpacing: '0.2em', textAlign: 'center',
          border: '1px solid var(--border-strong)', borderRadius: 8,
          background: 'var(--surface)',
        }}
      />
    </label>
  );
}
